import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Article, Deposant, LigneDemarque } from '@troc/shared'
import {
  anciennete,
  codeFamille,
  emojiArticle,
  eur,
  libelleDemarque,
  MOIS_FIN_CONTRAT,
  tauxEnPourcent,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { NiveauxDemarque } from '../composants/NiveauxDemarque'
import { PastilleStatut } from '../composants/Pastille'
import { imprimerEtiquettes } from '../impression/documents'
import { useToast } from '../etat/Toast'

interface VenteArticle {
  date: string
  ticketNo: string
  vendeurNom: string
  quantite: number
  prixCentimes: number
  prixReferenceCentimes: number
  taux: number
  commissionCentimes: number
  netDeposantCentimes: number
  reverse: boolean
}

interface Fiche {
  article: Article
  deposant: Deposant | null
  contrat: { num: string; creeLe: string; vendeurNom: string } | null
  demarque: LigneDemarque[]
  ventes: VenteArticle[]
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' })
const FORMAT_COURT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' })

function date(iso: string, format = FORMAT_DATE): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : format.format(d)
}

/** Une ligne d'état civil de l'article : intitulé à gauche, valeur à droite. */
function Ligne({ libelle, children }: { libelle: string; children: React.ReactNode }) {
  return (
    <div className="fiche-ligne">
      <span className="etiquette">{libelle}</span>
      <span className="valeur">{children}</span>
    </div>
  )
}

/**
 * La fiche d'un article, ouverte en touchant sa référence dans Article/Stock.
 *
 * Elle répond aux questions du comptoir dans l'ordre où elles se posent : à
 * qui est cette pièce, depuis quand est-elle là, à quel prix est-elle
 * aujourd'hui, et où en est son contrat.
 */
export function FicheArticle() {
  const { id } = useParams<{ id: string }>()
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [fiche, setFiche] = useState<Fiche | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [sortieOuverte, setSortieOuverte] = useState(false)
  const [envoiSortie, setEnvoiSortie] = useState(false)

  const charger = useCallback(() => {
    if (!id) return
    api<Fiche>(`/articles/${id}/fiche`)
      .then(setFiche)
      .catch((cause) =>
        setErreur(cause instanceof ErreurApi ? cause.message : 'Article introuvable.'),
      )
  }, [id])

  useEffect(charger, [charger])

  if (erreur) {
    return (
      <Ecran titre="Article" sousTitre="Introuvable">
        <div className="vide">{erreur}</div>
      </Ecran>
    )
  }
  if (!fiche) {
    return (
      <Ecran titre="Article" sousTitre="Chargement…">
        <div className="vide">Un instant…</div>
      </Ecran>
    )
  }

  const { article, deposant, contrat, demarque, ventes } = fiche
  const enRayon = article.statut !== 'vendu' && article.statut !== 'retire'
  const moisRestants = Math.max(0, MOIS_FIN_CONTRAT - article.mois)

  const sortir = async (motif: 'retire' | 'rendu' | 'donne') => {
    if (envoiSortie) return
    setEnvoiSortie(true)
    try {
      await api(`/articles/${article.id}/retirer`, { methode: 'POST', corps: { motif } })
      signaler(
        motif === 'retire'
          ? `${article.ref} retiré du rayon.`
          : motif === 'rendu'
            ? `${article.ref} marqué rendu au déposant.`
            : `${article.ref} marqué donné.`,
      )
      setSortieOuverte(false)
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Sortie impossible.')
    } finally {
      setEnvoiSortie(false)
    }
  }

  return (
    <Ecran
      titre={article.titre}
      sousTitre={`${article.ref} · ${codeFamille(article.categorie)} · ${article.categorie}`}
      outils={
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondaire"
            onClick={() => {
              imprimerEtiquettes([article])
              signaler('Étiquette envoyée à l’imprimante.')
            }}
          >
            Réimprimer l’étiquette
          </button>
          {enRayon && (
            <>
              <button type="button" className="btn btn-secondaire" onClick={() => setSortieOuverte(true)}>
                Sortir du rayon
              </button>
              <button
                type="button"
                className="btn btn-primaire"
                onClick={() => naviguer('/caisse', { state: { ajouterRef: article.ref } })}
              >
                Encaisser
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="zone-defilante fiche-article">
        <div className="fiche-colonnes">
          {/* ── Ce qu'il est ────────────────────────────────────────────── */}
          <section className="bloc-fiche">
            <h3 className="bloc-fiche-titre">L’article</h3>
            <Ligne libelle="Référence">
              <span className="ref">{article.ref}</span>
            </Ligne>
            <Ligne libelle="Famille">
              {codeFamille(article.categorie)} · {article.categorie}
            </Ligne>
            <Ligne libelle="Statut">
              <PastilleStatut statut={article.statut} />
            </Ligne>
            <Ligne libelle="Stock">
              {article.quantite > 1
                ? `${article.quantiteRestante} en rayon sur ${article.quantite} déposés`
                : article.quantiteRestante > 0
                  ? '1 exemplaire en rayon'
                  : 'Plus d’exemplaire en rayon'}
            </Ligne>
            <Ligne libelle="Commission">
              {article.tauxCommission === null
                ? 'Barème de la boutique'
                : `${tauxEnPourcent(article.tauxCommission)} — taux négocié au dépôt`}
            </Ligne>
            <Ligne libelle="Dévaluation">
              {article.demarqueActive
                ? 'Progressive — le prix baisse chaque mois'
                : 'Désactivée — le prix est gelé'}
            </Ligne>
          </section>

          {/* ── D'où il vient ──────────────────────────────────────────── */}
          <section className="bloc-fiche">
            <h3 className="bloc-fiche-titre">Le dépôt</h3>
            <Ligne libelle="Déposant">
              {deposant ? (
                <Link to={`/deposants/${deposant.id}`} style={{ color: 'var(--color-accent)' }}>
                  {deposant.nom}
                </Link>
              ) : (
                '—'
              )}
            </Ligne>
            <Ligne libelle="N° de client">{deposant?.ref ?? '—'}</Ligne>
            {deposant?.gsm && <Ligne libelle="GSM">{deposant.gsm}</Ligne>}
            <Ligne libelle="Contrat">
              {contrat ? (
                <Link to={`/contrats/${contrat.num}`} style={{ color: 'var(--color-accent)' }}>
                  {contrat.num}
                </Link>
              ) : (
                '—'
              )}
            </Ligne>
            <Ligne libelle="Déposé le">
              {date(article.deposeLe)}
              <span className="sourdine"> · il y a {anciennete(article.deposeLe)}</span>
            </Ligne>
            {contrat && <Ligne libelle="Reçu par">{contrat.vendeurNom}</Ligne>}
            <Ligne libelle="Ancienneté">
              {article.mois === 0 ? 'Premier mois' : `${article.mois + 1}ᵉ mois`}
              {enRayon &&
                ` · ${
                  moisRestants === 0
                    ? 'contrat échu, à récupérer'
                    : `${moisRestants} mois avant la fin du contrat`
                }`}
            </Ligne>
          </section>

          {/* ── Ce qu'il vaut ──────────────────────────────────────────── */}
          <section className="bloc-fiche">
            <h3 className="bloc-fiche-titre">Le prix</h3>
            <Ligne libelle="Prix d’origine">
              <span className="chiffre">{eur(article.prixInitialCentimes)}</span>
            </Ligne>
            <Ligne libelle="Prix en rayon">
              <span className="chiffre prix-fort">{eur(article.prixActuelCentimes)}</span>
            </Ligne>
            <Ligne libelle="Démarque">
              {!article.demarqueActive
                ? 'Aucune — dévaluation désactivée'
                : article.decoteManuelle !== null
                  ? `−${Math.round(article.decote * 100)} % posés à la main`
                  : libelleDemarque(article.mois)}
            </Ligne>
            {enRayon && (
              <div style={{ marginTop: 10 }}>
                <NiveauxDemarque
                  article={article}
                  onArticle={(maj) => setFiche({ ...fiche, article: maj })}
                  onErreur={alerter}
                />
              </div>
            )}
          </section>
        </div>

        {/* ── Le tableau signé au contrat ──────────────────────────────── */}
        <section className="bloc-fiche">
          <h3 className="bloc-fiche-titre">Démarque prévue au contrat</h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Mois</th>
                <th className="a-droite">Décote</th>
                <th className="a-droite">Prix</th>
              </tr>
            </thead>
            <tbody>
              {demarque.map((ligne) => (
                <tr
                  key={ligne.mois}
                  className={ligne.mois === article.mois + 1 ? 'ligne-corrigee' : undefined}
                >
                  <td>{ligne.libelle}</td>
                  <td className="a-droite">
                    {ligne.decote === 0 ? '—' : `−${Math.round(ligne.decote * 100)} %`}
                  </td>
                  <td className="a-droite chiffre">{eur(ligne.prixCentimes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ── Ce qu'il est devenu ──────────────────────────────────────── */}
        {ventes.length > 0 && (
          <section className="bloc-fiche">
            <h3 className="bloc-fiche-titre">Ventes</h3>
            <table className="tableau">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Ticket</th>
                  <th>Vendeur</th>
                  <th className="a-droite">Qté</th>
                  <th className="a-droite">Encaissé</th>
                  <th className="a-droite">Commission</th>
                  <th className="a-droite">Net déposant</th>
                  <th>Reversé</th>
                </tr>
              </thead>
              <tbody>
                {ventes.map((vente, i) => (
                  <tr key={i}>
                    <td>{date(vente.date, FORMAT_COURT)}</td>
                    <td className="ref">{vente.ticketNo}</td>
                    <td>{vente.vendeurNom}</td>
                    <td className="a-droite chiffre">{vente.quantite}</td>
                    <td className="a-droite chiffre">
                      {eur(vente.prixCentimes * vente.quantite)}
                      {vente.prixCentimes !== vente.prixReferenceCentimes && (
                        <span className="sourdine" title="Prix négocié au comptoir">
                          {' '}
                          ✎
                        </span>
                      )}
                    </td>
                    <td className="a-droite">
                      {tauxEnPourcent(vente.taux)} ·{' '}
                      {eur(vente.commissionCentimes * vente.quantite)}
                    </td>
                    <td className="a-droite chiffre">
                      {eur(vente.netDeposantCentimes * vente.quantite)}
                    </td>
                    <td>{vente.reverse ? 'Oui' : 'Pas encore'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      {sortieOuverte && (
        <ConfirmationSortie
          article={article}
          envoi={envoiSortie}
          onFermer={() => setSortieOuverte(false)}
          onChoisir={(motif) => void sortir(motif)}
        />
      )}
    </Ecran>
  )
}

const MOTIFS_SORTIE_UI: {
  cle: 'retire' | 'rendu' | 'donne'
  libelle: string
  detail: string
}[] = [
  {
    cle: 'retire',
    libelle: 'Retiré — obsolète, perdu, cassé',
    detail: 'Plus de valeur, plus présentable. Personne n’est payé.',
  },
  {
    cle: 'rendu',
    libelle: 'Rendu au déposant',
    detail: 'Remis en main propre — le déposant a repris sa pièce.',
  },
  {
    cle: 'donne',
    libelle: 'Donné',
    detail: 'Parti à la Croix-Rouge faute d’avoir été réclamé.',
  },
]

/**
 * Sortir un article sans vente est irréversible depuis l'écran : on choisit
 * le motif ici plutôt que de laisser deviner. Un seul écran de confirmation
 * suffit — le nom et la référence de la pièce y sont visibles, ce n'est pas
 * un geste qu'on fait sans les avoir sous les yeux.
 */
function ConfirmationSortie({
  article,
  envoi,
  onFermer,
  onChoisir,
}: {
  article: Article
  envoi: boolean
  onFermer: () => void
  onChoisir: (motif: 'retire' | 'rendu' | 'donne') => void
}) {
  return (
    <div className="voile voile-centre" onMouseDown={onFermer}>
      <div
        className="panneau-cloture"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>Sortir {article.ref} du rayon ?</h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        <div style={{ padding: '14px 18px 4px' }}>
          <div className="titre" style={{ fontSize: 16 }}>
            {article.titre}
          </div>
          <div className="sourdine" style={{ fontSize: 12 }}>
            Étiquette {eur(article.prixActuelCentimes)}
            {article.deposantNom && ` · ${article.deposantNom}`}
          </div>
        </div>

        <div style={{ padding: '10px 18px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {MOTIFS_SORTIE_UI.map((m) => (
            <button
              key={m.cle}
              type="button"
              className="btn btn-secondaire btn-large btn-bloc btn-centre"
              style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: 'auto', padding: '10px 16px' }}
              disabled={envoi}
              onClick={() => onChoisir(m.cle)}
            >
              <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 14 }}>
                {m.libelle}
              </span>
              <span className="sourdine" style={{ fontSize: 11, fontWeight: 400 }}>
                {m.detail}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
